/*
 * Copyright '2025' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { IManagedSessionCookieUserInfoService } from "sirius-platform-support-library/shared/authentication/handlers/dell-identity/managed-session-cookie/services/managed-session-cookie.user-info.service.interface";
import { IRequiredClaimsMappingDelegatedProvider } from "sirius-platform-support-library/shared/authentication/claims/required-claims-mapping.delegated-provider.interface";
import { RequiredClaims } from "sirius-platform-support-library/shared/authentication/claims/required-claims.enum";
import { ManagedSessionCookieUserInfoResponse } from "sirius-platform-support-library/shared/authentication/handlers/dell-identity/managed-session-cookie/services/managed-session-cookie.user-info.response";
import { environment } from "../../../environments/environments";
import { ExtendedClaims, UserClaimsKey } from "./extended-claims";
import { IManagedSessionCookieSessionService } from "sirius-platform-support-library/shared/authentication/handlers/dell-identity/managed-session-cookie/services/managed-session-cookie.session.service.interface";
import { ManagedSessionCookieAuthenticationConfigurator } from "sirius-platform-support-library/shared/authentication/handlers/dell-identity/managed-session-cookie/managed-session-cookie.authentication.configurator";
import { Session } from "sirius-platform-support-library/shared/authentication/session/session";
import { SessionWithClaims } from "sirius-platform-support-library/shared/authentication/session/session-with-claims";
import { KeyValuePair } from "sirius-platform-support-library/models/common";

export const TdmManagedSessionCookieUserInfoServiceTypeName =
  "TdmManagedSessionCookieUserInfoService";

export class TdmManagedSessionCookieUserInfoService
  implements
    IManagedSessionCookieUserInfoService,
    IManagedSessionCookieSessionService,
    IRequiredClaimsMappingDelegatedProvider
{
  private readonly window: Window;

  public constructor(window: Window) {
    this.window = window;
  }

  public getRequiredClaimsMappings(): Record<string, string> {
    const mappings: Record<string, string> = {};
    mappings[RequiredClaims.USER_IDENTIFIER] = UserClaimsKey.USER_IDENTIFIER;
    mappings[RequiredClaims.PRINCIPAL_NAME] = UserClaimsKey.PRINCIPAL_NAME;
    mappings[RequiredClaims.DISPLAY_NAME] = UserClaimsKey.DISPLAY_NAME;
    mappings[ExtendedClaims.USER_STATUS] = UserClaimsKey.USER_STATUS;
    mappings[ExtendedClaims.AUTHENTICATED_AS_IMPERSONATOR] =
      UserClaimsKey.AUTHENTICATED_AS_IMPERSONATOR;
    mappings[ExtendedClaims.TOU_ACTION] = UserClaimsKey.TOU_ACTION;
    mappings[ExtendedClaims.SETUP_COMPLETE] = UserClaimsKey.SETUP_COMPLETE;

    return mappings;
  }

  public async getUserInfo(): Promise<ManagedSessionCookieUserInfoResponse> {
    try {
      const response = await this.window.fetch(environment.userInfoUrl, {
        method: "GET",
        headers: {
          accept: "application/json",
        },
        cache: "no-cache",
        mode: "cors",
        credentials: "include",
        referrerPolicy: "no-referrer",
      });

      if (response.status !== 200) {
        throw new Error(
          `Fetch failed with ${response.status} status code. Details: ${response.statusText}`
        );
      }

      const json = await response.json();

      if (!json?.userStatus) {
        throw new Error("User is not authenticated");
      }

      const claims = Object.keys(json).map((key) => ({
        key: key,
        value: json[key],
      }));

      const session = this.buildSessionFromClaims(claims);

      return {
        claims: claims,
        session: session,
      };
    } catch (e) {
      console.error(
        "An exception has been encountered while trying to fetch user info",
        e
      );
      throw e;
    }
  }

  public async renewSession(
    configurator: ManagedSessionCookieAuthenticationConfigurator
  ): Promise<SessionWithClaims> {
    const userInfoResponse = await this.getUserInfo();

    const rawSession = this.buildSessionFromClaims(userInfoResponse.claims);

    return {
      ...rawSession,
      claims: userInfoResponse.claims,
    };
  }

  private buildSessionFromClaims(claims: KeyValuePair[]): Session {
    const issuedAt = new Date().getTime() / 1000;
    const expiresAt = issuedAt + 30 * 60;

    const mappings = this.getRequiredClaimsMappings();

    const principalName = claims.find(
      (claim) => claim.key === mappings[RequiredClaims.PRINCIPAL_NAME]
    )?.value;

    return {
      sessionId: principalName,
      profileId: principalName,
      issuedAt: issuedAt,
      expiresAt: expiresAt,
    };
  }
}
